import { Mode } from '@/context/ViewMode/type';

import request from '../request';

import {
  BankDetails,
  FinancialDataRecordType,
  OwnerContact,
  OwnerEventsResponseProps,
  OwnerInvoiceDetails,
  OwnerType,
  OwnerTypeUpdate,
  PropertyInvoiceBank,
  PropertyInvoiceBankAux,
  PropertyOwner,
  PropertyOwnerPatchProps,
  QueryParamsFilter,
  SeazoneEcosystemSlackRequest,
  SeazoneEnterpriseRequest,
} from './types';
import { getFinancialClosingDataFormatted } from './utils';

import { captureError } from '@/lib/sentry';

export const getPropertiesOwner = async (
  filters?: QueryParamsFilter | undefined,
  mode?: Mode,
): Promise<Array<PropertyOwner>> => {
  const userID = mode?.user_id || null;

  if (filters) {
    const params = {
      start_date: filters.start_date,
      end_date: filters.end_date,
      ordering: filters.ordering,
    };

    const { data } = await request.get('/properties/owner/', {
      params: {
        ...params,
        user_id: userID,
      },
    });
    return data;
  }

  const { data } = await request.get('/properties/owner/', {
    params: {
      user_id: userID,
    },
  });

  return data;
};

export const patchPropertiesOwner = async (
  propertyId: number,
  payload: PropertyOwnerPatchProps,
) => {
  const { data } = await request.patch(`/properties/owner/${propertyId}/`, {
    ...payload,
  });

  return data;
};

export const getFinancialClosing = async (
  propertyId: number | undefined,
  year: number,
  month_start: number,
  month_end: number,
  mode?: Mode | null,
) => {
  const userID = mode?.user_id || null;

  const params = {
    start_date: `${year}-${month_start <= 9 ? `0${month_start}` : month_start}`,
    end_date: `${year}-${month_end <= 9 ? `0${month_end}` : month_end}`,
  };

  const allParams = propertyId ? { property_id: propertyId, ...params } : { ...params };
  const { data } = await request.get('/financial_closing/', {
    params: {
      ...allParams,
      user_id: userID,
    },
  });

  if (propertyId) {
    return getFinancialClosingDataFormatted(propertyId, year, data);
  }

  const properties: number[] = Object.keys(data).map((id) => Number(id));
  let dataAllProperties: FinancialDataRecordType = {};

  properties.forEach((id) => {
    dataAllProperties = {
      ...dataAllProperties,
      ...getFinancialClosingDataFormatted(id, year, data),
    };
  });

  return dataAllProperties;
};

export const getOwnerEvents = async () => {
  const { data } = await request.get('/property/owner_events/');
  return data as OwnerEventsResponseProps;
};

export const postOwnerBank = async (params: BankDetails) => {
  const { data } = await request.post('/financial/owner/bank_details/', params);
  return data;
};

export const patchOwnerBank = async (params: BankDetails, id: number) => {
  const { data } = await request.patch(`/financial/owner/bank_details/${id}/`, params);
  return data;
};

export const getOwnerBank = async (mode?: Mode | null) => {
  const userID = mode?.user_id || null;
  const { data } = await request.get('/financial/owner/bank_details/', {
    params: {
      user_id: userID,
    },
  });
  return data as BankDetails[];
};

export const DeleteOwnerBank = async (id: number) => {
  const { data } = await request.delete(`/financial/owner/bank_details/${id}/`);
  return data;
};

export const getOwnerInvoice = async (mode?: Mode | null) => {
  const userID = mode?.user_id || null;
  const { data } = await request.get('/financial/owner/invoice_details/', {
    params: {
      user_id: userID,
    },
  });
  return data as OwnerInvoiceDetails[];
};

export const postOwnerInvoice = async (params: OwnerInvoiceDetails) => {
  const { data } = await request.post('/financial/owner/invoice_details/', params);
  return data;
};

export const patchOwnerInvoice = async (params: OwnerInvoiceDetails, id: number) => {
  const { data } = await request.patch(`/financial/owner/invoice_details/${id}/`, params);
  return data;
};

export const deleteOwnerInvoice = async (id: number) => {
  const { data } = await request.delete(`/financial/owner/invoice_details/${id}/`);
  return data;
};

export const getOwnerPropertyInvoiceBank = async (mode?: Mode) => {
  const userID = mode?.user_id || null;
  const { data } = await request.get('/property/bank_invoice_details/', {
    params: {
      user_id: userID,
    },
  });
  return data as PropertyInvoiceBank[];
};

export const putOwnerPropertyInvoiceBank = async (params: PropertyInvoiceBankAux, id: number) => {
  const { data } = await request.patch(`/property/bank_invoice_details/${id}/`, {
    bank_details: params?.bank_details === 0 ? undefined : params?.bank_details,
    invoice_details: params.invoice_details === 0 ? undefined : params?.invoice_details,
  });
  return data;
};

type GetOwnerContacts = {
  mode: Mode | null;
};
export const getOwnerContact = async ({ mode }: GetOwnerContacts) => {
  const userID = mode?.user_id || null;

  const { data } = await request.get('/account/owner_contacts/', {
    params: {
      user_id: userID,
    },
  });
  return data as OwnerContact[];
};

export const postOwnerContact = async (params: OwnerContact) => {
  const { data } = await request.post('/account/owner_contacts/', params);
  return data;
};

export const patchOwnerContact = async (params: OwnerContact, id: number) => {
  const { data } = await request.patch(`/account/owner_contacts/${id}/`, params);
  return data;
};

export const deleteOwnerContact = async (id: number) => {
  const { data } = await request.delete(`/account/owner_contacts/${id}/`);
  return data;
};

export const postOwner = async (params: OwnerType) => {
  const { data } = await request.post('/account/owner/', params);
  return data;
};

export const patchOwner = async (id: number, params: OwnerTypeUpdate) => {
  const { data } = await request.patch(`/account/owner/${id}/`, params);
  return data;
};

export const deleteOwner = async (id: number) => {
  const { data } = await request.delete(`/account/owner/${id}`);
  return data;
};

/**
 * Estes dois avisos iam do navegador direto para um webhook do Slack, e a URL
 * do webhook ia junto no JS público. O envio saiu do frontend: quem posta no
 * Slack é o sapron-backend (messaging/slack.py), que ainda não tem endpoint
 * para eles. Só os modais do card "Oportunidades Seazone" chamam estas funções,
 * e o card está desligado em OwnerPage/index.tsx. Se ele voltar sem o endpoint,
 * o proprietário vê uma mensagem dele e a tentativa fica registrada no Sentry
 * (sem os dados de contato).
 */
function envioIndisponivel(aviso: string): never {
  captureError({
    error: new Error(`${aviso}: sem endpoint no backend, o pedido não foi enviado`),
    module: 'OportunidadesSeazone',
    level: 'warning',
  });
  throw new Error(
    'Não conseguimos enviar seu pedido agora. Fale com a Seazone pelos canais de atendimento.',
  );
}

export const postSeazoneEnterprise: (
  dados: SeazoneEnterpriseRequest,
) => Promise<never> = async () => envioIndisponivel('Empreendimentos Seazone');

export const postSeazoneEcosystemSlackRequest: (
  dados: SeazoneEcosystemSlackRequest,
) => Promise<never> = async () => envioIndisponivel('Vender imóvel');

export const getOwners = async () => {
  const { data } = await request.get('/account/owner/');
  return data;
};
